<template>
  <div class="shell-property" data-testid="shell-property-section">
    <n-form label-placement="top" size="small">
      <n-form-item label="模式">
        <n-checkbox
          :checked="hollowWholeBody"
          data-testid="shell-hollow-whole-body-checkbox"
          @update:checked="handleHollowWholeBodyChange"
        >
          整体内部掏空
        </n-checkbox>
      </n-form-item>

      <n-form-item :label="hollowWholeBody ? '实体' : '移除面'">
        <div class="shell-face-summary">
          {{ faceSummary }}
        </div>
      </n-form-item>

      <n-form-item label="壁厚 (mm)">
        <n-input-number
          :value="thicknessMm"
          :min="0.001"
          :step="0.5"
          :show-button="true"
          data-testid="shell-thickness-input"
          @update:value="handleThicknessChange"
        />
      </n-form-item>

      <n-form-item label="方向">
        <n-select
          :value="direction"
          :options="directionOptions"
          data-testid="shell-direction-select"
          @update:value="handleDirectionChange"
        />
      </n-form-item>
    </n-form>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef, watch } from 'vue';
import { convertMToMm, convertMmToM } from '@puppycad/cad-core';
import type { ShellThicknessDirection } from '@puppycad/cad-core';
import { useMessage } from 'naive-ui';

/**
 * 抽壳属性面板。
 *
 * 设计说明：
 * 1. 面板只消费 property-manager 注入的 context，不直接持有抽壳状态；
 * 2. 底层长度单位是米，UI 显示按 mm，进出都经过 convertMmToM / convertMToMm 收口。
 */

interface ShellPropertySectionProps {
    context: {
        shellActive?: boolean;
        shellCanSubmit?: boolean;
        shellPreviewError?: string | null;
        hollowWholeBody?: boolean;
        shellDefinition?: {
            removedFaces?: Array<{ persistentRef: string }>;
            thickness?: number;
            direction?: ShellThicknessDirection;
            hollowWholeBody?: boolean;
        } | null;
        setHollowWholeBody?: (checked: boolean) => void;
        setShellThickness?: (value: number) => void;
        setShellDirection?: (value: ShellThicknessDirection) => void;
        clearShellPreviewError?: () => void;
        submitShell?: () => Promise<void> | void;
        cancelShell?: () => void;
    };
}

const props = defineProps<ShellPropertySectionProps>();
const context = toRef(props, 'context');
const message = useMessage();

// 预览求解失败时用顶部信息框提示，并清除控制器错误状态以便下一次失败可再次触发。
watch(
    () => context.value.shellPreviewError,
    (error) => {
        if (typeof error !== 'string' || error.length === 0) {
            return;
        }
        message.warning(error);
        context.value.clearShellPreviewError?.();
    },
);

const directionOptions = [
    { label: '朝内', value: 'inward' },
    { label: '朝外', value: 'outward' },
] satisfies Array<{ label: string; value: ShellThicknessDirection }>;

const hollowWholeBody = computed(() => (
    context.value.hollowWholeBody === true
    || context.value.shellDefinition?.hollowWholeBody === true
));

const thicknessMm = computed(() => Number(convertMToMm(
    context.value.shellDefinition?.thickness ?? convertMmToM(2),
).toFixed(3)));

const direction = computed<ShellThicknessDirection>(() => (
    context.value.shellDefinition?.direction ?? 'inward'
));

const faceSummary = computed(() => {
    if (hollowWholeBody.value) {
        return context.value.shellCanSubmit
            ? '已选择实体'
            : '点击模型选择实体';
    }
    const count = context.value.shellDefinition?.removedFaces?.length ?? 0;
    return count > 0 ? `已选择 ${count} 个面` : '尚未选择移除面';
});

function handleHollowWholeBodyChange(checked: boolean): void {
    context.value.setHollowWholeBody?.(checked);
}

function handleThicknessChange(value: number | null): void {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        return;
    }
    // value 是用户输入的毫米值；ShellFeatureController.setThicknessMm 内部负责 mm → m 转换。
    context.value.setShellThickness?.(value);
}

function handleDirectionChange(value: ShellThicknessDirection): void {
    context.value.setShellDirection?.(value);
}
</script>

<style scoped>
.shell-face-summary {
  min-height: 24px;
  line-height: 24px;
  color: var(--n-text-color-2);
}
</style>
